import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { AuthState, changePwBody, LoginRequest, LoginResponse } from '../types/auth';
import { Observable, of, tap, catchError, throwError, map } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TokenData } from '../types/tokenData';
import { jwtDecode } from 'jwt-decode';
import { ReferrerDoctor, Role, User } from '../types/user';
import { ExternalTokenData } from '../types/external-token';
import { EesztAuthService } from './eeszt-auth.service';

@Injectable({
  providedIn: 'root'
})
export class Auth {

  private readonly apiUrl = environment.apiUrl;

  private http = inject(HttpClient);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private injector = inject(Injector);

  tokenData = signal<TokenData | null>(null);
  currentUser = signal<User | null>(null);
  userLoading = signal<boolean>(false);
  externalTokenData = signal<ExternalTokenData | null>(null);

  // Staged login handed off from outside the login component (external-token
  // auto-login): the login page picks this up and shows its 2FA code step.
  pendingTwoFactor = signal<LoginResponse | null>(null);

  // External-token payload parked while the staged login waits for the 2FA
  // code; re-applied on successful verification so the request page receives
  // it exactly as in the non-2FA external login flow.
  private pendingExternalTokenData: ExternalTokenData | null = null;

  isLoggedIn = computed(() => {
    const token = this.tokenData();
    return token !== null && !this.isTokenExpired();
  });

  isTokenExpired = computed(() => {
    const token = this.tokenData();
    if (!token) return true;
    const now = Math.floor(Date.now() / 1000);
    return token.exp < now;
  });

  authState = computed((): AuthState => ({
    isLoggedIn: this.isLoggedIn(),
    user: this.currentUser(),
    token: this.tokenData(),
    isExpired: this.isTokenExpired()
  }));

  // Permission helper computed signals
  isAdmin = computed(() => {
    return this.hasPermission('ADMIN');
  });

  isSenderDoctor = computed(() => {
    return this.hasPermission('REF_PHYS');
  });

  isAssistant = computed(() => {
    return this.hasPermission('ASSISTANT');
  });

  isSender = computed(() => {
    return this.hasPermission('REF_PHYS') || this.hasPermission('ASSISTANT');
  });

  isReportingDoctorOrAssistant = computed(() => {
    return this.hasPermission('EXAM_PHYS');
  });

  isReportingDoctor = computed(() => {
    return this.hasPermission('DOCTOR');
  });

  isQualityControl = computed(() => {
    return this.hasPermission('START_QC') || this.hasPermission('EDIT_QC') || this.hasPermission('DELETE_QC');
  });

  constructor() {
  }

  // Permission helper method
  hasPermission(permissionCode: string): boolean {
    const user = this.currentUser();
    if (!user || !user.role || !user.role.permissions) {
      return false;
    }
    return user.role.permissions.some(permission => permission.code === permissionCode);
  }

  login(username: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/rest/auth/login`, { username, password, deviceInfo: "" } as LoginRequest).pipe(
      tap((response) => {
        // A direct (non-external) login invalidates any parked external
        // payload; loginWithExternalToken re-parks it right after this tap.
        this.pendingExternalTokenData = null;
        // Staged login: when 2FA is required no token is issued yet. The login
        // component reads the response and shows the second-factor step.
        if (response.twoFactorRequired) {
          return;
        }
        this.setAuthData(response.token!, true); // true = navigate after loading user
        this.toastr.success('Sikeres bejelentkezés');
      }),
      catchError((error) => {
        // this.toastr.error('Hiba történt a bejelentkezés során!');
        return throwError(() => error);
      })
    );
  }

  verifyLoginTwoFactor(preAuthToken: string, method: string, code: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/rest/auth/2fa/verify`, { preAuthToken, method, code }).pipe(
      tap((response) => {
        // Re-apply the external-token payload parked before the 2FA step, as a
        // fresh object so the consumer effects (patient/doctor prefill on the
        // request page) fire even if the original reference is still in the
        // signal. Must happen before setAuthData, which triggers navigation.
        if (this.pendingExternalTokenData) {
          this.externalTokenData.set({ ...this.pendingExternalTokenData });
          this.pendingExternalTokenData = null;
        }
        this.setAuthData(response.token!, true);
        if (response.recoveryUsed) {
          this.toastr.warning(
            'Helyreállító kóddal lépett be. A kétfaktoros azonosítás kikapcsolásra került — kérjük állítsa be újra.',
            'Kétfaktoros azonosítás kikapcsolva',
            { timeOut: 10000 }
          );
        } else {
          this.toastr.success('Sikeres bejelentkezés');
        }
      }),
      catchError((error) => throwError(() => error))
    );
  }

  sendLoginEmailCode(preAuthToken: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.apiUrl}/rest/auth/2fa/email/send`, { preAuthToken });
  }

  decodeExternalToken(token: string): ExternalTokenData | null {
    try {
      return jwtDecode(token) as ExternalTokenData;
    } catch (error) {
      console.error('Failed to decode external token:', error);
      return null;
    }
  }

  loginWithExternalToken(token: string): Observable<LoginResponse> {
    const tokenData = this.decodeExternalToken(token);
    if (!tokenData?.user) {
      return throwError(() => new Error('Invalid external token'));
    }

    // Store external token data for later use by other components
    this.externalTokenData.set(tokenData);

    // Perform login with embedded credentials
    return this.login(tokenData.user.username, tokenData.user.password).pipe(
      tap((response) => {
        // Staged login: park the payload so the successful 2FA verification
        // can re-apply it once the session is actually issued.
        if (response.twoFactorRequired) {
          this.pendingExternalTokenData = tokenData;
        }
      })
    );
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/rest/auth/logout`, {}).pipe(
      tap(() => {
        this.clearAuthData();
        this.toastr.info('Kijelentkezve');
      }),
      catchError(() => {
        // Even if server logout fails, clear local data
        this.clearAuthData();
        this.toastr.info('Kijelentkezve');
        return of(void 0);
      })
    );
  }

  logoutLocally(): void {
    this.clearAuthData();
    this.toastr.info('Kijelentkezve');
    this.router.navigate(['/']);
  }

  getToken(): string | null {
    const token = localStorage.getItem('token');
    return token;
  }

  /**
   * The referrer resolved for the operator's last case.
   *
   * An external token is consumed once: it is cleared after a submission and
   * when the request page is left. Remembering the referrer lets a second case
   * keep the same one without a fresh token, and lets the worklist scope
   * itself to that doctor straight after a reload.
   *
   * Stored: the seal number, plus the resolved id and name — the id is what
   * the worklist filters on, the name is what it labels the filter with, and
   * neither needs a lookup on a cold load. Address and phone are deliberately
   * not stored: the request page re-resolves the doctor from the API, so those
   * are never served from a stale copy.
   *
   * Bound to the operator it was recorded for and dropped on logout, so a
   * different user on the same workstation never inherits it.
   */
  private readonly REFERRER_SEAL_KEY = 'referrerSealNumber';

  /**
   * The referrer currently in force, shared across the app: the request page
   * sets it when it resolves one, the worklist reads its id to scope the list
   * to that doctor's cases. Cleared on logout.
   */
  referrerDoctor = signal<ReferrerDoctor | null>(null);

  rememberReferrer(sealNumber: string, doctor: User): void {
    const username = this.currentUser()?.username ?? this.tokenData()?.sub;
    if (!username || !sealNumber) return;

    try {
      localStorage.setItem(
        this.REFERRER_SEAL_KEY,
        JSON.stringify({ username, sealNumber, doctorId: doctor.id, doctorName: doctor.name })
      );
    } catch (error) {
      console.error('Failed to store referrer:', error);
    }
  }

  /**
   * The remembered referrer, or null when none was recorded for this
   * operator. `doctor` is absent on entries written before the id was cached;
   * callers fall back to resolving `sealNumber` through the API.
   */
  rememberedReferrer(): { sealNumber: string; doctor: ReferrerDoctor | null } | null {
    const username = this.currentUser()?.username ?? this.tokenData()?.sub;
    if (!username) return null;

    const stored = localStorage.getItem(this.REFERRER_SEAL_KEY);
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored) as {
        username?: string;
        sealNumber?: string;
        doctorId?: number;
        doctorName?: string;
      };
      // Recorded for somebody else: drop it rather than hand it over.
      if (parsed.username !== username || !parsed.sealNumber) {
        this.clearRememberedReferrerSealNumber();
        return null;
      }

      const doctor = typeof parsed.doctorId === 'number'
        ? { id: parsed.doctorId, name: parsed.doctorName ?? '' }
        : null;

      return { sealNumber: parsed.sealNumber, doctor };
    } catch {
      this.clearRememberedReferrerSealNumber();
      return null;
    }
  }

  clearRememberedReferrerSealNumber(): void {
    localStorage.removeItem(this.REFERRER_SEAL_KEY);
    this.referrerDoctor.set(null);
  }

  requestPasswordReset(identifier: string): Observable<void> {
    return this.http
      .post(this.apiUrl + '/rest/user/forgot/' + identifier, {}, { responseType: 'text' as const })
      .pipe(map(() => void 0));
  }

  changePassword(body: changePwBody, isActivationCode: boolean = false): Observable<void> {
    return this.http
      .put(this.apiUrl + '/rest/user/change-password' + (isActivationCode ? '/true' : '/false'), body, { responseType: 'text' as const })
      .pipe(map(() => void 0));
  }

  getUserByToken(): Observable<User> {
    return this.http.get<User>(this.apiUrl + '/rest/user/jwt');
  }

  private loadCurrentUser(navigate: boolean = false): void {
    this.userLoading.set(true);

    this.getUserByToken().pipe(
      tap((user) => {
        this.currentUser.set(user);
        this.userLoading.set(false);

        // Navigate based on role only if this is from login flow
        if (navigate && user?.role) {
          this.navigateByRole(user.role);
        }
      }),
      catchError((error) => {
        console.error('Failed to load user:', error);
        this.currentUser.set(null);
        this.userLoading.set(false);
        return of(null);
      })
    ).subscribe();
  }

  initializeAuth(navigate: boolean = false): void {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const tokenData = jwtDecode(token) as TokenData;
        const now = Math.floor(Date.now() / 1000);

        if (tokenData.exp < now) {
          this.clearAuthData();
          return;
        }

        this.tokenData.set(tokenData);
        this.loadCurrentUser(navigate);
      } catch (error) {
        console.error('Invalid token:', error);
        this.clearAuthData();
      }
    }
  }

  private setAuthData(token: string, navigate: boolean = false): void {
    localStorage.setItem('token', token);
    const tokenData = jwtDecode(token) as TokenData;
    this.tokenData.set(tokenData);
    this.loadCurrentUser(navigate);
  }

  private clearAuthData(): void {
    localStorage.removeItem('token');
    this.clearRememberedReferrerSealNumber();
    this.tokenData.set(null);
    this.currentUser.set(null);
    // Lazy inject to avoid circular dependency
    this.injector.get(EesztAuthService).clearToken();
  }

  navigateByRole(role: Role): void {
    // Admin routes
    // if (role.permissions.some(permission => permission.code === 'CREATE_SUBMITTER' || permission.code === 'CREATE_USER' || permission.code === 'EDIT_LAB_WHO' || permission.code === 'CREATE_EXAMINATION')) {
    if (this.isAdmin()) {
      this.router.navigate(['/admin']);
      return;
    }

    // Sender routes
    // if (role.permissions.some(permission => permission.code === 'CREATE_ORDER_AND_REQUISITION')) {
    if (this.isSender()) {
      this.router.navigate(['/reports/request/new'], { queryParamsHandling: 'merge' })
      return;
    }

    // Default route (validator)
    this.router.navigate(['/reports']);
  }
}
